import { useMutation, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  GetUserPreferenceResponse,
  LocaleSource,
  UpdateUserPreferenceCommandInput,
  UpdateUserPreferenceCommandOutput,
} from '@amzn/red-velvet-api';
import { getEnabledFeatures, useFeatures } from './features';
import { useUser } from './user';
import { getClient } from '../utils/getClient';
import { toOfferedLocale } from '../utils/localeUtils';
import { readCachedLocale, writeCachedLocale } from '../utils/localePreferenceCache';

export const GET_USER_PREFERENCE_QUERY_KEY = ['RedVelvetApi', 'getUserPreference'];
export const UPDATE_USER_PREFERENCE_MUTATION_KEY = ['RedVelvetApi', 'updateUserPreference'];

const PREFERENCE_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * The user's saved preferences. Today that is their language, taken from their own choice or their
 * Amazon profile, where no language means the client should fall back to the browser's.
 *
 * A language the user has already chosen this session is served from the cache, so the picker opens
 * showing it without waiting on a request.
 */
export function useGetUserPreference() {
  const features = useFeatures();
  const [searchParams] = useSearchParams();
  const { userId } = useUser();
  const client = getClient(getEnabledFeatures(features, searchParams));

  return useQuery<GetUserPreferenceResponse>({
    queryKey: GET_USER_PREFERENCE_QUERY_KEY,
    queryFn: async () => {
      const preference = await client.getUserPreference({});
      const offeredLocale = toOfferedLocale(preference.locale);
      if (offeredLocale) {
        writeCachedLocale(userId, offeredLocale);
      }
      return preference;
    },
    initialData: () => {
      const cachedLocale = readCachedLocale(userId);
      return cachedLocale
        ? { locale: cachedLocale, localeSource: LocaleSource.EXPLICIT_PREFERENCE }
        : undefined;
    },
    staleTime: PREFERENCE_STALE_TIME_MS,
  });
}

/** Saves the user's preferences so later visits, emails, and calendar invites all use them. */
export function useUpdateUserPreference() {
  const features = useFeatures();
  const [searchParams] = useSearchParams();
  const client = getClient(getEnabledFeatures(features, searchParams));

  return useMutation<UpdateUserPreferenceCommandOutput, Error, UpdateUserPreferenceCommandInput>({
    mutationKey: UPDATE_USER_PREFERENCE_MUTATION_KEY,
    mutationFn: (input) => client.updateUserPreference(input),
    // No retry, so a late success cannot contradict the error the user was just shown.
    retry: false,
  });
}
